import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts, fetchProductDetail, selectProductDetailState, selectProductsState } from '../../../redux/WithcookieSlice';
import { updateComponent } from '../../../redux/editorSlice';
import SafeImage from '../../common/SafeImage';
import { ProductSliderProps, ProductItem as ProductItemType } from './ProductSlider.types';
import { adaptProductSliderProps } from './ProductSlider.adapter';
import { useTranslation } from '../../../redux/TranslationHelper';
import './ProductSlider.css';

/**
 * 상품 슬라이더 컴포넌트
 */
const ProductSliderComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = adaptProductSliderProps(props);
  const { style = {}, className = '', id } = adaptedProps;
  
  // componentProps를 useMemo로 메모이제이션하여 렌더링마다 새로운 객체가 생성되는 것 방지
  const componentProps = useMemo(() => adaptedProps.componentProps || {}, [adaptedProps.componentProps]);
  
  // Redux hooks
  const dispatch = useDispatch();
  const productDetailState = useSelector(selectProductDetailState);
  const { products: reduxProducts, loading: reduxLoading } = useSelector(selectProductsState);
  
  // 상태 변수들
  const [products, setProducts] = useState<ProductItemType[]>([]);
  const [translateX, setTranslateX] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleEditValue, setTitleEditValue] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [innerContainerWidth, setInnerContainerWidth] = useState(0);
  
  // 스와이프 기능을 위한 상태
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const swipeThreshold = 50; // 스와이프로 인식할 최소 거리
  
  // 참조 변수들
  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(false);
  // 현재 필터 상태를 추적하는 참조 변수 추가
  const currentFilterRef = useRef<string>('');
  
  // 다국어 지원 훅 사용 (메모이제이션 적용하여 무한 렌더링 방지)
  const { t } = useTranslation();
  
  // 컴포넌트 프로퍼티 설정값 (에디터에서 수정 가능)
  const {
    sliderTitle = t('인기 상품'),
    showTitle = true,
    titleFontSize = '18px',
    titleFontWeight = '600',
    titleColor = '#333',
    itemsPerSlide = 4,
    autoSlide = true,
    autoSlideInterval = 3000,
    productIds = '', // 이전 버전과의 호환성을 위해 유지
    include_product_ids = '', // 포함할 상품 ID 목록
    exclude_product_ids = '', // 제외할 상품 ID 목록
    include_category_ids = '', // 포함할 카테고리 ID 목록
    exclude_category_ids = '', // 제외할 카테고리 ID 목록
    showStock = false // 재고 표시 여부 옵션
  } = componentProps;

  // 숫자값으로 변환이 필요한 속성들 변환
  const itemsPerSlideNum = parseInt(String(itemsPerSlide)) || 4;
  const autoSlideIntervalNum = parseInt(String(autoSlideInterval)) || 3000;
  
  // 타이틀 스타일 객체
  const titleStyle = {
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    color: titleColor
  };
  
  // 상품 ID 배열로 변환 (이전 방식과 새 방식 모두 지원) - useMemo로 메모이제이션
  const includeProductIds = useMemo(() => {
    
    // 없으면 빈 배열 반환
    if (!include_product_ids && !productIds) return [];
    
    // include_product_ids가 있으면 사용, 없으면 productIds 사용
    const sourceStr = include_product_ids || productIds;
    
    // 문자열인 경우만 처리
    if (typeof sourceStr !== 'string' || sourceStr.trim() === '') return [];
    
    // 콤마로 분리하여 배열로 변환
    return sourceStr.split(',').map(id => id.trim()).filter(id => id !== '');
  }, [include_product_ids, productIds, t]);
  
  // 필터링 옵션 배열로 변환 - 모두 useMemo로 메모이제이션
  const excludeProductIds = useMemo(() => {
    
    // 문자열이 없으면 빈 배열 반환
    if (!exclude_product_ids || typeof exclude_product_ids !== 'string' || exclude_product_ids.trim() === '') return [];
    
    // 콤마로 분리하여 배열로 변환
    return exclude_product_ids.split(',').map(id => id.trim()).filter(id => id !== '');
  }, [exclude_product_ids, t]);
  
  const includeCategoryIds = useMemo(() => {
    
    // 문자열이 없으면 빈 배열 반환
    if (!include_category_ids || typeof include_category_ids !== 'string' || include_category_ids.trim() === '') return [];
    
    // 콤마로 분리하여 배열로 변환
    return include_category_ids.split(',').map(id => id.trim()).filter(id => id !== '');
  }, [include_category_ids, t]);
  
  const excludeCategoryIds = useMemo(() => {
    
    // 문자열이 없으면 빈 배열 반환
    if (!exclude_category_ids || typeof exclude_category_ids !== 'string' || exclude_category_ids.trim() === '') return [];
    
    // 콤마로 분리하여 배열로 변환
    return exclude_category_ids.split(',').map(id => id.trim()).filter(id => id !== '');
  }, [exclude_category_ids, t]);

  // 콜백 함수들 - useCallback으로 메모이제이션하여 무한 루프 방지
  // 슬라이더 타이틀을 더블 클릭하면 편집 모드 켜기
  const handleTitleDoubleClick = useCallback(() => {
    setIsEditingTitle(true);
    setTitleEditValue(sliderTitle);
  }, [sliderTitle]);
  
  // 편집 모드에서 블러(focus 잃음) 이벤트가 발생하면 저장
  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    
    // 직접 dispatch를 호출하여 타이틀 업데이트
    dispatch(updateComponent({
      id,
      changes: {
        componentProps: {
          ...componentProps,
          sliderTitle: titleEditValue
        }
      }
    }));
  }, [id, componentProps, dispatch, titleEditValue]);

  // 터치 이벤트 핸들러 - useCallback으로 메모이제이션
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(null);
  }, []);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);
  
  // 슬라이드 이동 함수 - 단순화된 버전, useCallback으로 메모이제이션
  const moveSlide = useCallback((direction: 'next' | 'prev') => {
    if (!slideWidth || !innerContainerWidth) return;
    
    // 디바이스 유형에 따라 이동 거리 계산
    let moveDistance: number;
    if (isMobile) {
      // 모바일: 내부 너비를 1로 나눈 값으로 이동 (전체 너비)
      moveDistance = innerContainerWidth;
    } else if (isTablet) {
      // 태블릿: 내부 너비를 2로 나눈 값으로 이동 (절반 너비)
      moveDistance = innerContainerWidth / 2;
    } else {
      // PC: 내부 너비를 4로 나눈 값으로 이동 (1/4 너비)
      moveDistance = innerContainerWidth / 4;
    }
    
    if (direction === 'next') {
      // 다음 슬라이드로 이동 (오른쪽에서 왼쪽으로)
      setTranslateX(prev => prev - moveDistance);
    } else if (direction === 'prev') {
      // 이전 슬라이드로 이동 (왼쪽에서 오른쪽으로)
      setTranslateX(prev => prev + moveDistance);
    }
  }, [slideWidth, innerContainerWidth, isMobile, isTablet]);
  
  // handleTouchEnd 함수에 moveSlide 의존성 추가
  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > swipeThreshold;
    const isRightSwipe = distance < -swipeThreshold;
    
    if (isLeftSwipe) {
      moveSlide('next');
    }
    
    if (isRightSwipe) {
      moveSlide('prev');
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, moveSlide, swipeThreshold]);

  // 필터링 옵션 변경 핸들러 추가 - useCallback으로 메모이제이션
  const handleFilterOptionChange = useCallback((optionName: string, value: string) => {
    // Redux 상태 업데이트
    dispatch(updateComponent({
      id,
      changes: {
        componentProps: {
          ...componentProps,
          [optionName]: value
        }
      }
    }));
    
    // 필터 변경 시 데이터 리로드를 위해 fetchedRef 초기화
    fetchedRef.current = false;
    
    // 상품 데이터 다시 로드 표시 (API 호출은 useEffect에서 자동으로 수행됨)
    setLoading(true);
  }, [id, componentProps, dispatch]);

  // API 필터링 옵션 생성 함수 - useCallback으로 메모이제이션
  const getFilterOptions = useCallback(() => {
    const options: any = {
      page: 1,
      per_page: 100
    };



    // 모든 값을 숫자 배열로 변환하는 함수 - ProductList 컴포넌트와 동일하게 수정
    const toIntArray = (value: any): number[] => {
      // 값이 없으면 빈 배열 반환
      if (!value) return [];
      
      // 이미 배열인 경우
      if (Array.isArray(value)) {
        // 배열의 각 요소를 정수로 변환
        return value
          .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
          .filter(id => !isNaN(id));
      }
      
      // 문자열인 경우, 콤마로 분리하여 배열로 변환
      if (typeof value === 'string' && value.trim() !== '') {
        return value
          .split(',')
          .map(id => id.trim())
          .filter(id => id !== '')
          .map(id => parseInt(id, 10))
          .filter(id => !isNaN(id));
      }
      
      // 단일 값인 경우 정수로 변환하여 배열에 넣음
      const parsed = typeof value === 'string' ? parseInt(value, 10) : Number(value);
      return !isNaN(parsed) ? [parsed] : [];
    };
    
    // ID 관련 필드는 항상 정수 배열로 변환
    const includeProductIdsInt = toIntArray(includeProductIds);
    const excludeProductIdsInt = toIntArray(excludeProductIds);
    const includeCategoryIdsInt = toIntArray(includeCategoryIds);
    const excludeCategoryIdsInt = toIntArray(excludeCategoryIds);
    
  
    
    // 배열이 비어있지 않은 경우에만 옵션에 추가
    if (includeProductIdsInt.length > 0) {
      options.include_product_ids = includeProductIdsInt;
    }
    
    if (excludeProductIdsInt.length > 0) {
      options.exclude_product_ids = excludeProductIdsInt;
    }
    
    if (includeCategoryIdsInt.length > 0) {
      options.include_category_ids = includeCategoryIdsInt;
    }
    
    if (excludeCategoryIdsInt.length > 0) {
      options.exclude_category_ids = excludeCategoryIdsInt;
    }
    
    return options;
  }, [includeProductIds, excludeProductIds, includeCategoryIds, excludeCategoryIds, t]);

  // 상품 정보 가져오기
  useEffect(() => {
    // 초기 더미 데이터 설정
    const dummy: ProductItemType[] = [
      { id: 1, title: "스마트폰", price: 1200000, salePrice: 1000000, image: '' },
      { id: 2, title: "노트북", price: 1800000, image: '' },
      { id: 3, title: "이어버드", price: 250000, salePrice: 199000, image: '' },
      { id: 4, title: "워치", price: 350000, salePrice: 299000, image: '' },
      { id: 5, title: "태블릿", price: 950000, salePrice: 850000, image: '' },
      { id: 6, title: "헤드셋", price: 180000, salePrice: 150000, image: '' }
    ];

    // 필터링 옵션 설정
    const options = getFilterOptions();
    const filterOptionsString = JSON.stringify(options);
    
    // 필터가 변경되었는지 확인
    const isFilterChanged = currentFilterRef.current !== filterOptionsString;
    
    // 이미 데이터를 가져왔고 필터가 변경되지 않았으면 중복 요청 방지
    if (fetchedRef.current && !isFilterChanged) return;
    
    // 현재 필터 상태 업데이트
    currentFilterRef.current = filterOptionsString;
    
    setLoading(true);
    
    // 필터링 API를 사용하여 상품 목록 가져오기
    const fetchFilteredProducts = async () => {
      try {
        
        // Redux 액션 디스패치
        const resultAction = await dispatch(fetchProducts(options) as any);
        
        if (fetchProducts.fulfilled.match(resultAction)) {
          const responseData = resultAction.payload;
          const filteredProducts = responseData.products || [];
          
          if (filteredProducts && filteredProducts.length > 0) {
            fetchedRef.current = true;
            
            // 무한 슬라이딩을 위해 상품 배열 중복
            // 최소한 화면에 보이는 아이템 수의 3배 이상 있어야 안정적인 무한 슬라이딩 가능
            let repeatedProducts: ProductItemType[] = [];
            
            // 기준 아이템 개수보다 적을 경우, 원본 배열 중복 생성
            const minItems = 12; // PC에서 4개 표시 기준 3배
            let baseProducts = [...filteredProducts];
            
            // 필요한 만큼 배열 복제
            while (baseProducts.length < minItems) {
              baseProducts = [...baseProducts, ...filteredProducts];
            }
            
            // 최종 배열 생성 (3번 반복 - 충분한 슬라이딩 위해)
            repeatedProducts = [...baseProducts, ...baseProducts, ...baseProducts];
            
            setProducts(repeatedProducts);
          } else {
            // 더미 데이터도 반복
            const repeatedDummy = [...dummy, ...dummy, ...dummy];
            setProducts(repeatedDummy);
          }
        } else {
          throw new Error(t('상품 목록을 가져오는데 실패했습니다.'));
        }
      } catch (error) {
        console.error(t('[상품 슬라이더] 상품 정보 가져오기 실패:'), error);
        
        // 더미 데이터 반복
        const repeatedDummy = [...dummy, ...dummy, ...dummy];
        setProducts(repeatedDummy);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFilteredProducts();
    
    return () => {
      fetchedRef.current = false;
    };
  }, [dispatch, getFilterOptions, t, includeProductIds, excludeProductIds, includeCategoryIds, excludeCategoryIds]);

  // 모바일/태블릿 화면 감지 로직 추가
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };
    
    checkDeviceType();
    window.addEventListener('resize', checkDeviceType);
    
    return () => {
      window.removeEventListener('resize', checkDeviceType);
    };
  }, []);

  // 슬라이드 너비 계산
  useEffect(() => {
    if (containerRef.current) {
      const calculateWidths = () => {
        try {
          // 컨테이너의 실제 패딩값 가져오기
          const containerStyles = window.getComputedStyle(containerRef.current!);
          const paddingLeft = parseFloat(containerStyles.paddingLeft);
          const paddingRight = parseFloat(containerStyles.paddingRight);
          
          // 컨테이너의 내부 너비 (패딩 제외)
          const containerWidth = containerRef.current!.offsetWidth;
          const innerWidth = containerWidth - paddingLeft - paddingRight;
          
          // 내부 너비 저장
          setInnerContainerWidth(innerWidth);
          
          // 디바이스별 표시 개수
          let itemsPerRow = 4; // PC 기본값
          if (isMobile) {
            itemsPerRow = 1;
          } else if (isTablet) {
            itemsPerRow = 2;
          }
          
          // 정확한 슬라이드 너비 계산
          const exactSlideWidth = innerWidth / itemsPerRow;
          
          // 계산된 슬라이드 너비 저장
          setSlideWidth(exactSlideWidth);
          
          // 초기 위치 설정
          setIsTransitioning(false);
          setTranslateX(0);
          
          // 트랜지션 다시 켜기
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        } catch (error) {
          console.error(t('슬라이드 너비 계산 오류:'), error);
        }
      };
      
      calculateWidths();
      window.addEventListener('resize', calculateWidths);
      
      return () => {
        window.removeEventListener('resize', calculateWidths);
      };
    }
  }, [isMobile, isTablet, t]);

  // 무한 슬라이딩 효과 - 경계 도달 시 리셋
  useEffect(() => {
    if (!slideWidth || !innerContainerWidth || products.length === 0 || !sliderRef.current) return;
    
    // 기본 상품 세트 사이즈 (원본 배열 크기)
    const originalProductCount = products.length / 3; // 3번 반복했으므로 3으로 나눔
    
    // 앞부분 경계
    const forwardBoundary = -slideWidth * originalProductCount;
    
    // 뒷부분 경계
    const backwardBoundary = 0;
    
    // 슬라이더가 경계를 넘어갔는지 체크
    const checkBoundaries = () => {
      if (translateX <= forwardBoundary) {
        setIsTransitioning(false); // 트랜지션 끄기
        setTranslateX(translateX + originalProductCount * slideWidth); // 중간 세트로 이동
        
        // 트랜지션 다시 켜기
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      } else if (translateX > backwardBoundary) {
        // 뒤로 너무 많이 이동한 경우, 중간 세트로 순간 이동
        setIsTransitioning(false); // 트랜지션 끄기
        setTranslateX(translateX - originalProductCount * slideWidth); // 중간 세트로 이동
        
        // 트랜지션 다시 켜기
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }
    };
    
    // 트랜지션이 끝날 때 경계 체크
    const handleTransitionEnd = () => {
      checkBoundaries();
    };
    
    // 트랜지션 종료 이벤트 리스너 추가
    sliderRef.current.addEventListener('transitionend', handleTransitionEnd);
    
    return () => {
      if (sliderRef.current) {
        sliderRef.current.removeEventListener('transitionend', handleTransitionEnd);
      }
    };
  }, [translateX, slideWidth, innerContainerWidth, products.length, t]);

  // 자동 슬라이드 간격 설정
  useEffect(() => {
    if (!autoSlide || !slideWidth || !innerContainerWidth || products.length === 0) return;
    
    const interval = setInterval(() => {
      moveSlide('next');
    }, autoSlideIntervalNum);

    return () => clearInterval(interval);
  }, [autoSlide, autoSlideIntervalNum, innerContainerWidth, moveSlide, products.length, slideWidth]);

  // 형식 지정 함수 - useCallback으로 메모이제이션
  const formatPrice = useCallback((price: number) => {
    if (!price) return t('₩0');
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(price);
  }, [t]);

  // 필터링 설정 UI 렌더링 함수 - useCallback으로 메모이제이션
  const renderFilteringSettings = useCallback(() => {
    // 에디터 모드일 때만 필터링 설정 UI 보여주기
    const isEditMode = window.location.pathname.includes('/editor');
    if (!isEditMode) return null;
    
    return (
      <div className="product-slider-filtering-container">
        <h4 className="product-slider-filtering-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '8px'}}>
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
            <path d="M9 9V21" stroke="currentColor" strokeWidth="2" />
            <path d="M15 9V21" stroke="currentColor" strokeWidth="2" />
          </svg>
          {t("필터링 설정")}
        </h4>
        
        <div className="product-slider-filtering-input-group">
          <label className="product-slider-filtering-label">
            {t("포함할 상품 ID")}
          </label>
          <input 
            type="text" 
            value={include_product_ids || ''}
            onChange={(e) => handleFilterOptionChange('include_product_ids', e.target.value)}
            className="product-slider-filtering-input"
          />
          <div className="product-slider-filtering-help">
            {t("쉼표로 구분하여 입력 (예: 73,74,75)")}
          </div>
        </div>
        
        <div className="product-slider-filtering-input-group">
          <label className="product-slider-filtering-label">
            {t("제외할 상품 ID")}
          </label>
          <input 
            type="text" 
            value={exclude_product_ids || ''}
            onChange={(e) => handleFilterOptionChange('exclude_product_ids', e.target.value)}
            className="product-slider-filtering-input"
          />
          <div className="product-slider-filtering-help">
            {t("쉼표로 구분하여 입력 (예: 74,76)")}
          </div>
        </div>
        
        <div className="product-slider-filtering-input-group">
          <label className="product-slider-filtering-label">
            {t("포함할 카테고리 ID")}
          </label>
          <input 
            type="text" 
            value={include_category_ids || ''}
            onChange={(e) => handleFilterOptionChange('include_category_ids', e.target.value)}
            className="product-slider-filtering-input"
          />
          <div className="product-slider-filtering-help">
            {t("쉼표로 구분하여 입력 (예: 14,8)")}
          </div>
        </div>
        
        <div className="product-slider-filtering-input-group">
          <label className="product-slider-filtering-label">
            {t("제외할 카테고리 ID")}
          </label>
          <input 
            type="text" 
            value={exclude_category_ids || ''}
            onChange={(e) => handleFilterOptionChange('exclude_category_ids', e.target.value)}
            className="product-slider-filtering-input"
          />
          <div className="product-slider-filtering-help">
            {t("쉼표로 구분하여 입력 (예: 16,17)")}
          </div>
        </div>
      </div>
    );
  }, [t, include_product_ids, exclude_product_ids, include_category_ids, exclude_category_ids, handleFilterOptionChange]);

  if (loading) {
    return <div className="product-slider-loading" />;
  }

  // 컨테이너 스타일에 props 스타일 병합
  const containerStyle = {
    backgroundColor: style.backgroundColor || 'transparent',
    borderWidth: style.borderWidth || '1px',
    borderColor: style.borderColor || '#eeeeee',
    borderRadius: style.borderRadius || '0px',
    boxShadow: style.boxShadow || 'none',
    padding: style.padding || '0 15px',
    paddingTop: style.paddingTop || '0',
    paddingRight: style.paddingRight || '15px',
    paddingBottom: style.paddingBottom || '0',
    paddingLeft: style.paddingLeft || '15px',
    margin: style.margin || '0',
    marginTop: style.marginTop || '0',
    marginRight: style.marginRight || '0',
    marginBottom: style.marginBottom || '0',
    marginLeft: style.marginLeft || '0',
    ...style
  };

  // 상품이 없는 경우 메시지 표시
  if (!products || products.length === 0) {
    return (
      <div 
        ref={containerRef} 
        className={`product-slider-container ${className}`} 
        style={containerStyle}
      >
        {showTitle && (
          isEditingTitle ? (
            <input
              value={titleEditValue}
              onChange={(e) => setTitleEditValue(e.target.value)}
              onBlur={handleTitleBlur}
              style={{
                ...titleStyle,
                width: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                padding: '0 10px',
                margin: '0 0 30px 0'
              }}
              autoFocus
            />
          ) : (
            <h3 
              className="product-slider-title"
              style={titleStyle} 
              onDoubleClick={handleTitleDoubleClick}
            >
              {sliderTitle}
            </h3>
          )
        )}
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {t("표시할 상품이 없습니다.")}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className={`product-slider-container ${className}`} 
      style={containerStyle}
    >
      {showTitle && (
        isEditingTitle ? (
          <input
            value={titleEditValue}
            onChange={(e) => setTitleEditValue(e.target.value)}
            onBlur={handleTitleBlur}
            style={{
              ...titleStyle,
              width: '100%',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              padding: '0 10px',
              margin: '0 0 30px 0'
            }}
            autoFocus
          />
        ) : (
          <h3 
            className="product-slider-title"
            style={titleStyle}
            onDoubleClick={handleTitleDoubleClick}
          >
            {sliderTitle}
          </h3>
        )
      )}
      
      <button className="product-slider-prev-button" onClick={() => moveSlide('prev')} />
      <button className="product-slider-next-button" onClick={() => moveSlide('next')} />
      
      <div className="product-slider-mask">
        <div 
          ref={sliderRef} 
          className={`product-slider-wrapper ${!isTransitioning ? 'no-transition' : ''}`}
          style={{ transform: `translateX(${translateX}px)` }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {products.map((product, index) => {
            const deviceClass = isMobile ? 'mobile' : isTablet ? 'tablet' : 'pc';
            const hasImage = Boolean(product.image || product.thumbnail || product.config?.img_url);
            
            return (
              <div 
                key={`${product.id}-${index}`}
                className={`product-slider-slide ${deviceClass}`}
                ref={index === 0 ? slideRef : null}
              >
                <div className={`product-slider-item ${hasImage ? 'has-image' : ''}`}>
                  <Link to={`/products/${product.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="product-slider-image-container">
                      {React.createElement(SafeImage as any, {
                        src: product.image || product.thumbnail || product.config?.img_url,
                        alt: t(product.title || '') || t(product.name || '') || t('상품명 없음'),
                        style: { 
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        },
                        placeholderStyle: {
                          width: "100%",
                          height: "100%",
                          backgroundColor: "#CCCCCC",
                          position: "absolute",
                          top: 0,
                          left: 0
                        }
                      })}
                    </div>
                    <div className="product-slider-info">
                      <h3 className="product-slider-product-title">
                        {t(product.title || product.name || '') || t('상품명 없음')}
                      </h3>
                      <div className="product-slider-price">
                        {product.salePrice || product.config?.discounted_price ? (
                          <>
                            {formatPrice(Number(product.salePrice || product.config?.discounted_price))}
                            <span className="product-slider-sale-price">
                              {formatPrice(Number(product.price || product.config?.default_price))}
                            </span>
                          </>
                        ) : (
                          formatPrice(Number(product.price || product.config?.default_price || 0))
                        )}
                      </div>
                      {showStock && product.config?.stock_count !== undefined && (
                        <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                          {t("재고")}: {product.config?.stock_count}{t("개")}
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {renderFilteringSettings()}
    </div>
  );
};

export default ProductSliderComponent; 